/**
 * Attendance V2 — T&D sheet page (2026-02 rebuild).
 *
 * Owner: everyone's records, person + date-range filters, per-person
 * summaries, pending-corrections approval queue, full audit trail.
 * Everyone else: locked to "My attendance only" + request-correction flow.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CalendarRange, MapPin, ChevronDown, ChevronRight, Wrench, Check, X, Clock, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { attendanceV2API, workspaceAPI } from '../lib/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import CorrectionModal from '../components/attendance/CorrectionModal';
import { StellarDateRangePicker } from '../components/StellarDateRangePicker';
import {
  localDateStr, fmtTime, fmtMinutes, fmtDateLong, breakMinutes, STATUS_META,
  fmtTimeInZone, tzAbbrev,
} from '../lib/attendanceV2';

/** Preset ranges (user's local timezone). Week runs Monday–Sunday. */
function presetRange(preset) {
  const now = new Date();
  const today = localDateStr(now);
  if (preset === 'today') return { start: today, end: today };
  if (preset === 'week') {
    const mon = new Date(now);
    mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(sun.getDate() + 6);
    return { start: localDateStr(mon), end: localDateStr(sun) };
  }
  if (preset === 'biweekly') {
    const d = new Date(now);
    d.setDate(d.getDate() - 13);
    return { start: localDateStr(d), end: today };
  }
  if (preset === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: localDateStr(first), end: localDateStr(last) };
  }
  return null; // custom
}

const STATUS_CLS = {
  clocked_in: 'bg-emerald-500/15 text-emerald-400',
  on_break: 'bg-amber-500/15 text-amber-400',
  clocked_out: 'bg-sky-500/15 text-sky-400',
};

function LocationCell({ loc }) {
  if (!loc) return <span className="text-muted-foreground/40">—</span>;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground truncate max-w-[180px]"
      title={loc.address || `${loc.lat}, ${loc.lng}`}>
      <MapPin className="h-3 w-3 shrink-0 text-gold/60" />
      <span className="truncate">{loc.address || `${loc.lat?.toFixed?.(3)}, ${loc.lng?.toFixed?.(3)}`}</span>
    </span>
  );
}

/** Owner approval queue for pending manual corrections. */
function PendingCorrections({ wsId, onResolved }) {
  const [pending, setPending] = useState([]);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await attendanceV2API.pendingCorrections(wsId);
      setPending(res.data.pending || []);
    } catch { /* panel is best-effort */ }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (item, approve) => {
    setBusyId(item.edit_id);
    try {
      const note = notes[item.edit_id] || '';
      if (approve) await attendanceV2API.approveCorrection(item.record_id, item.edit_id, note);
      else await attendanceV2API.rejectCorrection(item.record_id, item.edit_id, note);
      toast.success(approve ? 'Correction approved and applied' : 'Correction rejected');
      await load();
      onResolved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the correction.');
    } finally {
      setBusyId('');
    }
  };

  if (!pending.length) return null;
  return (
    <div className="border border-amber-500/25 bg-amber-500/[0.04] rounded-lg p-4 space-y-3" data-testid="av2-pending-panel">
      <h2 className="text-[13px] font-semibold text-amber-400">
        Pending corrections ({pending.length})
      </h2>
      {pending.map((p) => {
        const ch = p.proposed_changes || {};
        return (
          <div key={p.edit_id} className="rounded-md bg-foreground/[0.03] p-3 space-y-2" data-testid={`av2-pending-${p.edit_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[13px] font-medium">{p.user_name} · {fmtDateLong(p.date)}</span>
              <span className="text-[11px] text-muted-foreground">requested {fmtTime(p.edit_time)}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 text-[12px]">
              <div className="space-y-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Current</span>
                <div>In {fmtTime(p.current?.clock_in_time)} · Out {fmtTime(p.current?.clock_out_time)}</div>
                <div className="text-muted-foreground">{(p.current?.breaks || []).length} break(s)</div>
              </div>
              <div className="space-y-0.5">
                <span className="text-[10px] uppercase tracking-wide text-gold">Proposed</span>
                <div>
                  {ch.clock_in_time && <>In → <b>{fmtTime(ch.clock_in_time)}</b> · </>}
                  {ch.clock_out_time && <>Out → <b>{fmtTime(ch.clock_out_time)}</b></>}
                  {!ch.clock_in_time && !ch.clock_out_time && <span className="text-muted-foreground">times unchanged</span>}
                </div>
                {ch.breaks && (
                  <div className="text-muted-foreground">
                    breaks → {ch.breaks.map((b, i) => `${fmtTime(b.break_start_time)}–${fmtTime(b.break_end_time)}`).join(', ') || 'none'}
                  </div>
                )}
              </div>
            </div>
            <p className="text-[12px] italic text-muted-foreground">“{p.reason_text}”</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Input value={notes[p.edit_id] || ''} placeholder="Optional note…"
                onChange={(e) => setNotes((n) => ({ ...n, [p.edit_id]: e.target.value }))}
                className="h-7 text-[11px] max-w-[220px]" data-testid={`av2-pending-note-${p.edit_id}`} />
              <Button size="sm" disabled={busyId === p.edit_id} onClick={() => resolve(p, true)}
                className="h-7 text-[11px] bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30"
                data-testid={`av2-approve-${p.edit_id}`}>
                <Check className="h-3 w-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="ghost" disabled={busyId === p.edit_id} onClick={() => resolve(p, false)}
                className="h-7 text-[11px] text-red-400 hover:bg-red-500/15" data-testid={`av2-reject-${p.edit_id}`}>
                <X className="h-3 w-3 mr-1" /> Reject
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Expanded audit trail for a single day row. */
function RowDetail({ rec, tz }) {
  return (
    <tr className="bg-foreground/[0.02]">
      <td colSpan={8} className="px-4 py-3">
        <div className="grid md:grid-cols-2 gap-4 text-[12px]">
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Breaks</span>
            {(rec.breaks || []).length === 0 && <div className="text-muted-foreground/50">No breaks</div>}
            {(rec.breaks || []).map((b, i) => (
              <div key={b.id || i} className="flex items-center gap-2" data-testid={`av2-break-detail-${rec.id}-${i}`}>
                <Clock className="h-3 w-3 text-amber-400/70" />
                <span>
                  {fmtTimeInZone(b.break_start_time, tz)} → {b.break_end_time ? fmtTimeInZone(b.break_end_time, tz) : 'open'}
                  {b.break_end_time && <span className="text-gold/80"> · {fmtMinutes(breakMinutes(b))}</span>}
                </span>
                {b.auto_gap && <span className="text-[9px] px-1 rounded bg-sky-500/15 text-sky-400">auto gap</span>}
                {b.auto_closed && <span className="text-[9px] px-1 rounded bg-red-500/15 text-red-400">auto-closed</span>}
                {b.manual && <span className="text-[9px] px-1 rounded bg-gold/15 text-gold">corrected</span>}
              </div>
            ))}
          </div>
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Corrections / audit trail</span>
            {(rec.manual_edits || []).length === 0 && <div className="text-muted-foreground/50">No corrections</div>}
            {(rec.manual_edits || []).map((e) => (
              <div key={e.id} className="rounded bg-foreground/[0.03] p-2 space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{e.edited_by_name}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase ${
                    e.approval_status === 'approved' ? 'bg-emerald-500/15 text-emerald-400'
                      : e.approval_status === 'rejected' ? 'bg-red-500/15 text-red-400'
                        : 'bg-amber-500/15 text-amber-400'}`}>
                    {e.approval_status}
                  </span>
                  <span className="text-muted-foreground text-[10px]">{fmtTime(e.edit_time)}</span>
                </div>
                <div className="text-muted-foreground italic">“{e.reason_text}”</div>
                {e.proposed_changes?.clock_in_time && (
                  <div>In: {fmtTime(e.original_values?.clock_in_time)} → {fmtTime(e.proposed_changes.clock_in_time)}</div>
                )}
                {e.proposed_changes?.clock_out_time && (
                  <div>Out: {fmtTime(e.original_values?.clock_out_time)} → {fmtTime(e.proposed_changes.clock_out_time)}</div>
                )}
                {e.owner_note && <div className="text-[11px]">Owner note: {e.owner_note}</div>}
              </div>
            ))}
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function AttendanceV2Page() {
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const { user } = useAuth();
  const wsId = currentWorkspace?.id;
  const isOwner = user?.role === 'super_admin' || workspaceRole === 'owner';

  const [preset, setPreset] = useState('today');
  const [start, setStart] = useState(localDateStr());
  const [end, setEnd] = useState(localDateStr());
  const [person, setPerson] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [members, setMembers] = useState([]);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  // Timezone view: 'member' shows each row's times in that member's own zone
  // (rows' dates are ALWAYS the member's local day); 'mine' = viewer's zone.
  const [tzView, setTzView] = useState('member');
  const rowTz = (r) => (tzView === 'member' ? r.tz || undefined : undefined);
  const [fixRecord, setFixRecord] = useState(null);

  useEffect(() => {
    if (wsId && isOwner) {
      workspaceAPI.listMembersLite(wsId).then((r) => setMembers(r.data || [])).catch(() => {});
    }
  }, [wsId, isOwner]);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const uid = isOwner ? (person === 'all' ? '' : person) : '';
      const [sheet, sum] = await Promise.all([
        attendanceV2API.sheet(wsId, start, end, uid),
        attendanceV2API.summary(wsId, start, end, uid),
      ]);
      setRecords(sheet.data.records || []);
      setSummary(sum.data.summary || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the attendance sheet.');
    } finally {
      setLoading(false);
    }
  }, [wsId, start, end, person, isOwner]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => summary.reduce(
    (a, s) => ({ worked: a.worked + (s.worked_minutes || 0), brk: a.brk + (s.break_minutes || 0) }),
    { worked: 0, brk: 0 },
  ), [summary]);

  const applyPreset = (p) => {
    setPreset(p);
    const r = presetRange(p);
    if (r) { setStart(r.start); setEnd(r.end); }
  };

  const exportPdf = async () => {
    if (exporting || !wsId) return;
    setExporting(true);
    try {
      const uid = isOwner ? person : '';
      const res = await attendanceV2API.reportPdf(wsId, start, end, uid, new Date().getTimezoneOffset());
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-report-${start}-to-${end}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Attendance report downloaded');
    } catch {
      toast.error('Could not export the PDF, please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl" data-testid="av2-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-gold" /> Attendance
          </h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            {isOwner ? 'Full team attendance — training & development view.' : 'My attendance only.'}
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          {isOwner && (
            <Select value={person} onValueChange={setPerson}>
              <SelectTrigger className="h-8 w-[180px] text-[12px]" data-testid="av2-person-filter">
                <SelectValue placeholder="Everyone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="h-8 w-[140px] text-[12px]" data-testid="av2-range-preset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="biweekly">Last 2 weeks</SelectItem>
              <SelectItem value="month">This month</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
          <StellarDateRangePicker
            value={{ start, end }}
            onChange={({ start: s, end: e }) => { setStart(s); setEnd(e); setPreset('custom'); }}
            testId="av2-range-picker"
          />
          <Select value={tzView} onValueChange={setTzView}>
            <SelectTrigger className="h-8 w-[170px] text-[12px]" data-testid="av2-tz-view">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Times: member's zone</SelectItem>
              <SelectItem value="mine">Times: my timezone</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" disabled={exporting}
            onClick={exportPdf}
            className="h-8 text-[12px] border-gold/30 text-gold hover:bg-gold/10"
            data-testid="av2-export-pdf">
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {exporting ? 'Exporting…' : 'Export as PDF'}
          </Button>
        </div>
      </div>

      {isOwner && wsId && <PendingCorrections wsId={wsId} onResolved={load} />}

      <p className="text-[11px] text-muted-foreground -mt-2" data-testid="av2-tz-note">
        Each row's date is that member's <span className="text-gold">own local day</span> (IST members' "July 9" is their July 9).
        The date range filters by those local days. Days present &amp; totals are computed per member's local day.
      </p>

      {/* Summary chips */}
      <div className="flex gap-2 flex-wrap" data-testid="av2-summary">
        {summary.map((s) => (
          <div key={s.user_id} className="rounded-lg border border-border/50 px-3 py-2 text-[12px]" data-testid={`av2-summary-${s.user_id}`}>
            <div className="font-medium">{s.user_name || 'Unknown'}</div>
            <div className="text-muted-foreground">
              Days present: {s.days} · Total worked: {fmtMinutes(s.worked_minutes)} · Total breaks: {fmtMinutes(s.break_minutes)}
              {s.days > 0 && <> · Avg/day: {fmtMinutes(s.worked_minutes / s.days)}</>}
            </div>
          </div>
        ))}
        {summary.length > 1 && (
          <div className="rounded-lg border border-gold/30 bg-gold/[0.05] px-3 py-2 text-[12px]">
            <div className="font-medium text-gold">Total</div>
            <div className="text-muted-foreground">worked {fmtMinutes(totals.worked)} · breaks {fmtMinutes(totals.brk)}</div>
          </div>
        )}
      </div>

      {/* Sheet */}
      <div className="border border-border/50 rounded-lg overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/50">
              <th className="px-3 py-2 w-6" />
              <th className="px-3 py-2">Date</th>
              {isOwner && <th className="px-3 py-2">Person</th>}
              <th className="px-3 py-2">Clock in</th>
              <th className="px-3 py-2">Clock out</th>
              <th className="px-3 py-2">Breaks</th>
              <th className="px-3 py-2">Worked</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Fix</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && records.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground" data-testid="av2-empty">
                No attendance records in this range yet.
              </td></tr>
            )}
            {!loading && records.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="border-b border-border/30 hover:bg-foreground/[0.02]" data-testid={`av2-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="text-muted-foreground hover:text-foreground" data-testid={`av2-expand-${r.id}`}>
                      {expanded === r.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    {fmtDateLong(r.date)}
                    {r.tz && tzAbbrev(r.tz) && (
                      <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-foreground/[0.06] text-muted-foreground align-middle"
                            title={r.tz} data-testid={`av2-tz-chip-${r.id}`}>
                        {tzAbbrev(r.tz)}
                      </span>
                    )}
                  </td>
                  {isOwner && <td className="px-3 py-2 whitespace-nowrap">{r.user_name}</td>}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div>{fmtTimeInZone(r.clock_in_time, rowTz(r))}</div>
                    <LocationCell loc={r.clock_in_location} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div>{fmtTimeInZone(r.clock_out_time, rowTz(r))}</div>
                    <LocationCell loc={r.clock_out_location} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" data-testid={`av2-breaks-total-${r.id}`}>
                    {(r.breaks || []).length ? fmtMinutes(r.total_break_minutes) : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium">{fmtMinutes(r.total_worked_minutes)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_CLS[r.status] || 'bg-foreground/[0.06] text-muted-foreground'}`}>
                      {STATUS_META[r.status]?.label || r.status}
                    </span>
                    {r.has_pending_edit && (
                      <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400" data-testid={`av2-pending-chip-${r.id}`}>
                        fix pending
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {(isOwner || r.user_id === user?.id) && (
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] text-gold hover:bg-gold/10"
                        onClick={() => setFixRecord(r)} data-testid={`av2-fix-${r.id}`}>
                        <Wrench className="h-3 w-3 mr-1" /> Fix
                      </Button>
                    )}
                  </td>
                </tr>
                {expanded === r.id && <RowDetail rec={r} tz={rowTz(r)} />}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <CorrectionModal
        open={!!fixRecord}
        onOpenChange={(v) => !v && setFixRecord(null)}
        record={fixRecord}
        isOwner={isOwner}
        onSubmitted={load}
      />
    </div>
  );
}
